import { createSelector } from '@reduxjs/toolkit';

import { createAppSelector } from 'wanderu-ducks-utils';

import { selectAppStateSlice } from '../selectors';
import NAMESPACE from './types';

function _selectData(appState) {
    return selectAppStateSlice(appState)[NAMESPACE];
}

function _mapCarrierData(carrierData) {
    if (!carrierData) {
        return undefined;
    }

    const {
        carrier_id,
        features,
        livedetails,
        name,
        supplementalinfo,
        ticketDelivery,
        vehicle_type,
        book,
        preferred_currency,
        canonical_slug,
        groups = {},
    } = carrierData;
    const {
        business,
    } = groups;

    const carrierFeatures = features && Object.entries(features)
        .reduce((featureSet, [key, value]) => {
            return value
                ? featureSet.concat(key)
                : featureSet;
        }, []);

    return {
        businessGroup: business,
        carrierCode: carrier_id,
        features: carrierFeatures,
        liveDetails: livedetails,
        maxSeats: book
            ? book.seats_max
            : 1,
        name,
        pageSlug: canonical_slug,
        preferredCurrency: preferred_currency,
        supplementalInfo: supplementalinfo,
        ticketDelivery,
        vehicleType: vehicle_type,
    };
}

function _mapMultipleCarrierData(data, carrierCodes) {
    return carrierCodes.reduce((map, carrierCode) => ({
        ...map,
        [carrierCode]: _mapCarrierData(data[carrierCode]),
    }), {});
}

const selectCarrierData = createAppSelector(
    [
        _selectData,
        (_, carrierCodes) => carrierCodes,
    ],
    (data, carrierCodes) => {
        if (!carrierCodes) {
            return undefined;
        }
        if (typeof carrierCodes === 'string') {
            return _mapCarrierData(data[carrierCodes]);
        }
        return _mapMultipleCarrierData(data, carrierCodes);
    },
);

const selectCarrierCollection = createSelector(
    [_selectData],
    (allCarrierData) => {
        return Object.entries(allCarrierData)
            .reduce((collection, [code, rawData]) => ({
                ...collection,
                [code]: _mapCarrierData(rawData),
            }), {});
    },
);

function selectRequiresSupplementalTripInfo(appState, carrierCode) {
    const { supplementalInfo = {} } = selectCarrierData(appState, carrierCode) || {};
    return !!supplementalInfo.tripinfo;
}

export {
    selectCarrierCollection,
    selectCarrierData,
    selectRequiresSupplementalTripInfo,
};
